import { lazy, type ReactNode, Suspense, useMemo } from 'react';

import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { Spinner } from '@app/components/ui/spinner';
import { TooltipProvider } from '@app/components/ui/tooltip';
import ErrorFallback from '@cloud-ui/components/ErrorFallback';
import PageShell from '@cloud-ui/components/PageShell';
import { ROUTES } from '@cloud-ui/constants';
import {
  AuthProvider,
  RequireAuth,
  RequireRedteamAccess,
  useAuth,
} from '@cloud-ui/contexts/AuthContext';
import { ConfigProvider, useConfig } from '@cloud-ui/contexts/ConfigContext';
import { ConfiguredFusionAuth } from '@cloud-ui/contexts/FusionAuthContext';
import { RequireSelectedTeam, TeamsProvider } from '@cloud-ui/contexts/TeamsContext';
import { ToastProvider } from '@cloud-ui/contexts/ToastContext';

import { ErrorBoundary } from 'react-error-boundary';
import {
  Navigate,
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import DocumentTitle from './components/DocumentTitle';
import { RbacProvider } from './contexts/RbacContext';

// Lazy-loaded page components for code splitting
const ApiTokensPage = lazy(() => import('@cloud-ui/pages/apiTokens/ApiTokensPage'));
const LoginPage = lazy(() => import('@cloud-ui/pages/auth/login/Login'));
const LogoutPage = lazy(() => import('@cloud-ui/pages/auth/logout/Logout'));
const ResultsPage = lazy(() => import('@cloud-ui/pages/eval/page'));
const PublicEvalGuard = lazy(() => import('@cloud-ui/pages/eval/PublicEvalGuard'));
const NoAccessPage = lazy(() => import('@cloud-ui/pages/NoAccess'));
const NotFoundPage = lazy(() => import('@cloud-ui/pages/NotFound'));
const RedteamConfigsListPage = lazy(() => import('@cloud-ui/pages/redteam/configs/list'));
const RedteamDashboardPage = lazy(() => import('@cloud-ui/pages/redteam/index'));
const RedteamReportPage = lazy(() => import('@cloud-ui/pages/redteam/report/page'));
const RemediationReportPage = lazy(() => import('@cloud-ui/pages/redteam/report/remediation/page'));
const CreateScanPage = lazy(() => import('@cloud-ui/pages/redteam/scan'));
const WelcomePage = lazy(() => import('@cloud-ui/pages/welcome/page'));

const LoggedInResetPasswordPage = lazy(() => import('./pages/auth/logged-in-reset-password/page'));
const ChangelogPage = lazy(() => import('./pages/changelog/page'));
const OAuthCallback = lazy(() => import('./pages/auth/OauthCallback'));
const Register = lazy(() => import('./pages/auth/register'));
const DatasetsPage = lazy(() => import('./pages/datasets/page'));
const EvalsIndexPage = lazy(() => import('./pages/evals/page'));
const PromptsPage = lazy(() => import('./pages/prompts/page'));
const GuardrailsDashboard = lazy(() => import('./pages/guardrails/page'));
const GuardrailsListPage = lazy(() => import('./pages/guardrails/list/page'));
const GuardrailDetailPage = lazy(() => import('./pages/guardrails/[id]/page'));
const GuardrailGeneratePoliciesPage = lazy(
  () => import('./pages/guardrails/[id]/generate-policies/page'),
);
const GuardrailRequestsPage = lazy(() => import('./pages/guardrails/requests/page'));
const GuardrailsDashboardPage = lazy(() => import('./pages/guardrails/dashboard/page'));
const GuardrailTargetsPage = lazy(() => import('./pages/guardrails/targets/page'));
const GuardrailTargetDetailPage = lazy(() => import('./pages/guardrails/targets/[id]/page'));
const Jobs = lazy(() => import('./pages/jobs/jobs'));
const ModelAuditConfigurationPage = lazy(
  () => import('./pages/model-audit/ModelAuditConfigurationPage'),
);
const ModelAuditHistoryPage = lazy(() => import('./pages/model-audit/ModelAuditHistoryPage'));
const ModelAuditScanDetails = lazy(() => import('./pages/model-audit/ModelAuditScanDetails'));
const ModelAuditPage = lazy(() => import('./pages/model-audit/page'));
const NoTeamPage = lazy(() => import('./pages/NoTeam'));
const OrganizationPage = lazy(() => import('./pages/organization'));
const AuditLogsPage = lazy(() => import('./pages/organization/audit-logs'));
const CreateOrganizationPage = lazy(() => import('./pages/organization/create'));
const OrganizationDetails = lazy(() => import('./pages/organization/organization'));
const OrganizationOverviewPage = lazy(() => import('./pages/organization/overview/page'));
const RolesList = lazy(() => import('./pages/organization/roles'));
const RoleDetails = lazy(() => import('./pages/organization/roles/[id]'));
const TeamDetails = lazy(() => import('./pages/organization/teams/[id]'));
const OrganizationTeams = lazy(() => import('./pages/organization/teams/list'));
const OrgUsagePage = lazy(() => import('./pages/organization/usage/[id]'));
const ProbeUsagePage = lazy(() => import('./pages/organization/probe-usage'));
const OrganizationUsers = lazy(() => import('./pages/organization/users'));
const MediaStoragePage = lazy(() => import('./pages/organization/media-storage'));
const WebhooksListPage = lazy(() => import('./pages/organization/webhooks'));
const RedteamConfigsEditPage = lazy(() => import('./pages/redteam/configs/edit/edit'));
const IssuesPage = lazy(() => import('./pages/redteam/issues/page'));
const CustomPoliciesPage = lazy(
  () => import('./pages/redteam/plugins/customPolicies/CustomPoliciesPage'),
);
const CustomPolicyMutationPage = lazy(
  () => import('./pages/redteam/plugins/customPolicies/CustomPolicyMutationPage'),
);
const EditPluginSetPage = lazy(() => import('./pages/redteam/plugins/edit'));
const PluginSetsListPage = lazy(() => import('./pages/redteam/plugins/list'));
const PluginsPage = lazy(() => import('./pages/redteam/plugins/PluginsPage'));
const ExternalGuidelinesListPage = lazy(
  () => import('./pages/redteam/externalGuidelines/ExternalGuidelinesListPage'),
);
const ExternalGuidelineDetailPage = lazy(
  () => import('./pages/redteam/externalGuidelines/ExternalGuidelineDetailPage'),
);
const ResultTagsPage = lazy(() => import('./pages/evals/result-tags'));
const PluginSeverityOverridesMutationPage = lazy(
  () =>
    import('./pages/redteam/plugins/pluginSeverityOverrides/PluginSeverityOverridesMutationPage'),
);
const PluginSeverityOverridesPage = lazy(
  () => import('./pages/redteam/plugins/pluginSeverityOverrides/PluginSeverityOverridesPage'),
);
const EditTargetPage = lazy(() => import('./pages/redteam/targets/edit'));
const TargetListPage = lazy(() => import('./pages/redteam/targets/list'));
const TeamRedteamProviderPage = lazy(() => import('./pages/redteam/teamProvider/page'));
const ServerSettingsEditPage = lazy(() => import('./pages/serverSettings/edit'));
const ServerSettingsPage = lazy(() => import('./pages/serverSettings/page'));
const CodeScanSetupPage = lazy(() => import('./pages/codeScan/setup'));
const CodeScanFeedbackPage = lazy(() => import('./pages/codeScan/FeedbackPage'));
const UpgradePage = lazy(() => import('./pages/upgrade/page'));
const EvalCreatorPage = lazy(() => import('./pages/eval/create/page'));
const EvalConfigsPage = lazy(() => import('./pages/eval/configs/page'));
const ProvidersListPage = lazy(() => import('./pages/providers/list'));
const EditProviderPage = lazy(() => import('./pages/providers/edit'));
const ReportsLayout = lazy(() => import('./pages/reports/ReportsLayout'));
const ReportsIndexRedirect = lazy(() =>
  import('./pages/reports/ReportsLayout').then((module) => ({
    default: module.ReportsIndexRedirect,
  })),
);
const TeamActivityReportPage = lazy(() => import('./pages/reports/TeamActivityReportPage'));
const VulnerabilityPostureReportPage = lazy(
  () => import('./pages/reports/VulnerabilityPostureReportPage'),
);

// Intake pages (public form only - admin pages are in admin-app)
const IntakeFormPage = lazy(() => import('./pages/intake/page'));
const LazyModePage = lazy(() => import('./pages/lazy-mode/LazyModePage'));
// Setup wizard (public route for first-boot configuration)
const SetupWizard = lazy(() => import('./pages/setup/SetupWizard'));

const ON_PREM_DEFAULT_ROUTE = ROUTES.jobs;
const PROMPTFOO_CLOUD_DEFAULT_ROUTE = ROUTES.redteam.targets;
const LEGACY_MODEL_AUDIT_SCAN_ROUTE = ROUTES.modelAuditScanDetails.replace(
  '/scan/:scanId',
  '/:scanId',
);
const MODEL_AUDIT_CONFIGURATION_SEGMENT = ROUTES.modelAuditConfiguration.replace(
  `${ROUTES.modelAudit}/`,
  '',
);
const MODEL_AUDIT_HISTORY_SEGMENT = ROUTES.modelAuditHistory.replace(`${ROUTES.modelAudit}/`, '');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});
const ProtectedRoutes = () => (
  <RequireAuth>
    <RequireSelectedTeam>
      <Outlet />
    </RequireSelectedTeam>
  </RequireAuth>
);

const RedteamRestrictedRoutes = () => (
  <RequireRedteamAccess>
    <Outlet />
  </RequireRedteamAccess>
);

export function BrowserTelemetryRoutes({ children }: { children: ReactNode }) {
  const { browserTelemetryReady, disableTelemetry } = useConfig();
  const TelemetryRoutes = useMemo(
    () => (browserTelemetryReady && !disableTelemetry ? Sentry.withSentryRouting(Routes) : Routes),
    [browserTelemetryReady, disableTelemetry],
  );

  return <TelemetryRoutes>{children}</TelemetryRoutes>;
}

const SETUP_BYPASS_PATH_PREFIXES = [
  ROUTES.login,
  ROUTES.logout,
  ROUTES.upgrade,
  ROUTES.codeScan.setup,
  '/code-scan/feedback/',
  '/auth/fusionauth/callback',
  '/intake/',
] as const;

function isSetupBypassPath(pathname: string): boolean {
  return SETUP_BYPASS_PATH_PREFIXES.some((pathPrefix) => pathname.startsWith(pathPrefix));
}

export function SetupRedirectGuard({ children }: { children: ReactNode }) {
  const { config, isLoading: configLoading } = useConfig();
  const location = useLocation();

  if (configLoading) {
    return <div></div>;
  }

  if (
    config?.needsSetup &&
    !location.pathname.startsWith(ROUTES.setup) &&
    !isSetupBypassPath(location.pathname)
  ) {
    return <Navigate to={ROUTES.setup} replace />;
  }

  return <>{children}</>;
}

function SetupAwareShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const content = (
    <ErrorBoundary
      resetKeys={[location.key]}
      fallbackRender={({ error }) => <ErrorFallback error={error} />}
    >
      {children}
    </ErrorBoundary>
  );

  if (location.pathname.startsWith(ROUTES.setup)) {
    return content;
  }

  return <PageShell>{content}</PageShell>;
}

function LegacyModelAuditScanRedirect() {
  const { scanId } = useParams<{ scanId: string }>();

  if (!scanId) {
    return <Navigate to={ROUTES.modelAuditHistory} replace />;
  }

  return <Navigate to={ROUTES.modelAuditScanDetails.replace(':scanId', scanId)} replace />;
}

const AuthenticatedRootRedirect = () => {
  const { organization } = useAuth();
  const { config } = useConfig();

  return (
    <Navigate
      to={
        organization?.canUseRedteam
          ? config?.isPromptfooCloud
            ? PROMPTFOO_CLOUD_DEFAULT_ROUTE
            : ON_PREM_DEFAULT_ROUTE
          : ROUTES.eval
      }
      replace
    />
  );
};

function WrappedApp() {
  const { isLoading, isAdmin } = useAuth();
  const { browserTelemetryReady, config, isLoading: configLoading } = useConfig();
  const waitingForEnabledTelemetry =
    Boolean(config) && config?.disableTelemetry !== true && !browserTelemetryReady;

  if (isLoading || configLoading || waitingForEnabledTelemetry) {
    return <div></div>;
  }

  return (
    <>
      <DocumentTitle />
      <SetupRedirectGuard>
        <SetupAwareShell>
          <Suspense
            fallback={
              <div className="flex min-h-[400px] items-center justify-center">
                <Spinner size="lg" />
              </div>
            }
          >
            <BrowserTelemetryRoutes>
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <AuthenticatedRootRedirect />
                  </RequireAuth>
                }
              />

              {/* Public routes */}
              <Route path={ROUTES.login} element={<LoginPage />} />
              <Route path={ROUTES.logout} element={<LogoutPage />} />
              <Route path={ROUTES.register} element={<Register />} />

              {/* Setup wizard - public route for first-boot configuration */}
              <Route path={ROUTES.setup} element={<SetupWizard />} />

              {/* FusionAuth Routes */}
              <Route path={ROUTES.oauthCallback} element={<OAuthCallback />} />

              <Route path={ROUTES.upgrade} element={<UpgradePage />} />

              {/* Code Scan Setup - Public route for GitHub App installation flow */}
              <Route path={ROUTES.codeScan.setup} element={<CodeScanSetupPage />} />

              {/* Code Scan Feedback - Public route for PR comment feedback */}
              <Route path={ROUTES.codeScan.feedback} element={<CodeScanFeedbackPage />} />

              {/* Intake Form - Public route for customers to fill out intake questionnaire */}
              <Route path="/intake/:token" element={<IntakeFormPage />} />

              {/* Evals and Reports are private but can be made public by the user */}
              <Route path={`${ROUTES.eval}/:id`} element={<PublicEvalGuard />} />
              <Route path={`${ROUTES.redteam.report}/:id`} element={<RedteamReportPage />} />
              <Route
                path={`${ROUTES.redteam.report}/:id/remediation`}
                element={<RemediationReportPage />}
              />
              <Route path={ROUTES.evals} element={<EvalsIndexPage />} />
              <Route path={ROUTES.datasets} element={<DatasetsPage />} />

              <Route path={ROUTES.organization.new} element={<CreateOrganizationPage />} />

              {/* Protected routes */}
              <Route element={<ProtectedRoutes />}>
                {/* Auth & Account */}
                <Route path={ROUTES.welcome} element={<WelcomePage />} />
                <Route path={ROUTES.apiTokens} element={<ApiTokensPage />} />
                <Route path={ROUTES.changelog} element={<ChangelogPage />} />
                <Route
                  path={ROUTES.loggedInPasswordReset}
                  element={<LoggedInResetPasswordPage />}
                />
                <Route>
                  <Route
                    path={`${ROUTES.organization.index}/:orgId`}
                    element={<OrganizationPage />}
                  >
                    <Route
                      index
                      element={<Navigate to={isAdmin ? 'details' : 'teams'} replace />}
                    />
                    {isAdmin && (
                      <>
                        <Route path="details" element={<OrganizationDetails />} />
                        <Route path="users" element={<OrganizationUsers />} />
                        <Route path="webhooks" element={<WebhooksListPage />} />
                        <Route path="media-storage" element={<MediaStoragePage />} />
                        <Route path="roles">
                          <Route index element={<RolesList />} />
                          <Route path=":roleId" element={<RoleDetails />} />
                        </Route>
                        <Route path="usage" element={<OrgUsagePage />} />
                        <Route path="probe-usage" element={<ProbeUsagePage />} />
                        <Route path="audit-logs" element={<AuditLogsPage />} />
                      </>
                    )}
                    <Route path="teams">
                      <Route index element={<OrganizationTeams />} />
                      <Route path=":teamId" element={<TeamDetails />} />
                    </Route>
                    <Route
                      path="result-tags"
                      element={<Navigate to={ROUTES.evalResultTags} replace />}
                    />
                  </Route>

                  {config?.enableServerSettingsPage && (
                    <Route path={ROUTES.serverSettings.index} element={<ServerSettingsPage />}>
                      <Route index element={<ServerSettingsEditPage />} />
                      <Route
                        path={`${ROUTES.serverSettings.edit}/:key`}
                        element={<ServerSettingsEditPage />}
                      />
                      <Route element={<RedteamRestrictedRoutes />}>
                        <Route path="team-provider" element={<TeamRedteamProviderPage />} />
                      </Route>
                    </Route>
                  )}
                </Route>

                {/* Core Features */}
                <Route path={ROUTES.eval} element={<ResultsPage />} />
                <Route path={ROUTES.evalResultTags} element={<ResultTagsPage />} />
                <Route
                  path="/redteam/result-tags"
                  element={<Navigate to={ROUTES.evalResultTags} replace />}
                />
                <Route path={ROUTES.prompts} element={<PromptsPage />} />

                {/* Eval Creator (behind runtime config) */}
                {config?.evalCreatorEnabled === true && (
                  <>
                    <Route path={ROUTES.evalCreate} element={<EvalCreatorPage />} />
                    <Route path={`${ROUTES.evalCreate}/:configId`} element={<EvalCreatorPage />} />
                    <Route path={ROUTES.evalConfigs} element={<EvalConfigsPage />} />
                  </>
                )}

                <Route path={ROUTES.providers} element={<ProvidersListPage />} />
                <Route path={ROUTES.providerEdit} element={<EditProviderPage />} />

                {!config?.isPromptfooCloud && (
                  <Route
                    path={ROUTES.adminJobs}
                    element={isAdmin ? <Jobs mode="admin" /> : <NoAccessPage />}
                  />
                )}

                {/* Enterprise routes */}
                <Route element={<RedteamRestrictedRoutes />}>
                  {/* Guardrails */}
                  <Route path={ROUTES.guardrails.index} element={<GuardrailsDashboard />} />
                  <Route path={ROUTES.guardrails.dashboard} element={<GuardrailsDashboardPage />} />
                  <Route path={ROUTES.guardrails.list} element={<GuardrailsListPage />} />
                  <Route path={ROUTES.guardrails.requests} element={<GuardrailRequestsPage />} />
                  <Route path={`${ROUTES.guardrails.list}/:id`} element={<GuardrailDetailPage />} />
                  <Route
                    path={`${ROUTES.guardrails.list}/:id/generate-policies`}
                    element={<GuardrailGeneratePoliciesPage />}
                  />
                  <Route path={ROUTES.guardrails.targets} element={<GuardrailTargetsPage />} />
                  <Route
                    path={`${ROUTES.guardrails.targets}/:id`}
                    element={<GuardrailTargetDetailPage />}
                  />
                  <Route
                    path={`${ROUTES.guardrails.targets}/:id/integrate`}
                    element={<GuardrailTargetDetailPage />}
                  />
                  <Route path={ROUTES.redteam.index} element={<RedteamDashboardPage />} />
                  <Route path={ROUTES.redteam.report} element={<RedteamReportPage />} />

                  {/* Configs */}
                  <Route path={ROUTES.redteam.configs} element={<RedteamConfigsListPage />} />
                  <Route path={ROUTES.redteam.createConfig} element={<RedteamConfigsEditPage />} />
                  <Route
                    path={`${ROUTES.redteam.configs}/:id`}
                    element={<RedteamConfigsEditPage />}
                  />

                  {/* Plugins */}
                  <Route path={ROUTES.redteam.plugins} element={<PluginsPage />}>
                    <Route index element={<PluginSetsListPage />} />
                    <Route path=":id" element={<EditPluginSetPage />} />
                    <Route
                      path={ROUTES.redteam.pluginSeverityOverrides}
                      element={<PluginSeverityOverridesPage />}
                    />
                    <Route
                      path={`${ROUTES.redteam.pluginSeverityOverrides}/:id`}
                      element={<PluginSeverityOverridesMutationPage />}
                    />
                    <Route path={ROUTES.redteam.customPolicies} element={<CustomPoliciesPage />}>
                      <Route path=":id" element={<CustomPolicyMutationPage />} />
                    </Route>
                  </Route>

                  {/* External Guidelines */}
                  <Route
                    path={ROUTES.redteam.externalGuidelines}
                    element={<ExternalGuidelinesListPage />}
                  />
                  <Route
                    path={`${ROUTES.redteam.externalGuidelines}/:id`}
                    element={<ExternalGuidelineDetailPage />}
                  />
                  {/* Jobs (on-prem only) */}
                  {!config?.isPromptfooCloud && (
                    <>
                      <Route path={ROUTES.jobs} element={<Jobs />} />
                      <Route path={`${ROUTES.jobs}/:id`} element={<Jobs />} />
                    </>
                  )}

                  {/* Model Audit */}
                  <Route
                    path={`${ROUTES.modelAuditScanDetails}`}
                    element={<ModelAuditScanDetails />}
                  />
                  <Route
                    path={LEGACY_MODEL_AUDIT_SCAN_ROUTE}
                    element={<LegacyModelAuditScanRedirect />}
                  />
                  <Route path={ROUTES.modelAudit} element={<ModelAuditPage />}>
                    <Route
                      index
                      element={<Navigate to={MODEL_AUDIT_CONFIGURATION_SEGMENT} replace />}
                    />
                    <Route
                      path={MODEL_AUDIT_CONFIGURATION_SEGMENT}
                      element={<ModelAuditConfigurationPage />}
                    />
                    <Route path={MODEL_AUDIT_HISTORY_SEGMENT} element={<ModelAuditHistoryPage />} />
                  </Route>

                  {/* Vulnerabilities */}
                  <Route path={ROUTES.redteam.vulnerabilities} element={<IssuesPage />} />
                  <Route
                    path={`${ROUTES.redteam.vulnerabilities}/:issueId`}
                    element={<IssuesPage />}
                  />

                  {/* Targets */}
                  <Route path={ROUTES.redteam.targetSetup} element={<LazyModePage />} />
                  <Route
                    path={`${ROUTES.redteam.targetSetup}/:targetId`}
                    element={<LazyModePage />}
                  />
                  <Route path={ROUTES.redteam.targets} element={<TargetListPage />} />
                  <Route path={`${ROUTES.redteam.targets}/:id`} element={<EditTargetPage />} />
                  <Route
                    path={ROUTES.redteam.teamProvider}
                    element={<Navigate to={ROUTES.serverSettings.teamRedteamProvider} replace />}
                  />

                  {/* Create Scan */}
                  <Route path={ROUTES.redteam.createScan} element={<CreateScanPage />} />

                  {/* Organization Overview: */}
                  <Route
                    path={ROUTES.organization.overview}
                    element={<OrganizationOverviewPage />}
                  />

                  {/* Reports */}
                  <Route path={ROUTES.reports.index} element={<ReportsLayout />}>
                    <Route index element={<ReportsIndexRedirect />} />
                    <Route path="team-activity" element={<TeamActivityReportPage />} />
                    <Route
                      path="vulnerability-posture"
                      element={<VulnerabilityPostureReportPage />}
                    />
                  </Route>

                  {/* Dashboard catch-all routes - MUST be last to avoid matching specific routes */}
                  <Route
                    path={`${ROUTES.redteam.index}/:targetId`}
                    element={<RedteamDashboardPage />}
                  />
                  <Route
                    path={ROUTES.redteam.target.base + '/:tab'}
                    element={<RedteamDashboardPage />}
                  />
                </Route>
              </Route>

              {/* Catch-all + explicit entry for 404 Not Found */}
              <Route path={ROUTES.noAccess} element={<NoAccessPage />} />
              <Route path={ROUTES.notFound} element={<NotFoundPage />} />
              <Route path={ROUTES.noTeam} element={<NoTeamPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </BrowserTelemetryRoutes>
          </Suspense>
        </SetupAwareShell>
      </SetupRedirectGuard>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider>
        <ConfiguredFusionAuth>
          <AuthProvider>
            <ToastProvider>
              <TeamsProvider>
                <Router>
                  <RbacProvider>
                    <TooltipProvider>
                      <WrappedApp />
                    </TooltipProvider>
                  </RbacProvider>
                </Router>
              </TeamsProvider>
            </ToastProvider>
          </AuthProvider>
        </ConfiguredFusionAuth>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
